import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { COUNTRIES } from '@/utils/geoData';
import { CountryCitySelector } from '@/components/CountryCitySelector';
import { CURRENCIES_LIST, ccySym, hasCardPayable } from '@/utils/currencyUtils';
import { calcShipmentMinPrice, ZERO_DEC_CCY, STATIC_RATES_EUR } from '@/utils/tripPriceUtils';
import { canDo } from '@/utils/verifications';
import VerificationGate from '@/components/verification/VerificationGate';
import { isEntreprise, isEntreprisePending } from '@/utils/entreprise';
import '@/styles/single-form.css';
import { BACKEND_URL } from '@/config';
import { compressImage } from '@/utils/photos';

const API = `${BACKEND_URL}/api`;

// ─── Données statiques ────────────────────────────────────────────────────────

const URGENCY_HINTS = {
  low:  { label: 'Standard',    hint: 'Délai habituel accepté.' },
  med:  { label: 'Prioritaire', hint: 'Priorisé dans la file de matching.' },
  high: { label: 'Urgent',      hint: 'À transporter dans les 48h — visibilité maximum.' },
};

const CATEGORIES = [
  'Vêtements / Textile', 'Électronique', 'Documents administratifs',
  'Cosmétiques / Hygiène', 'Médicaments / Santé', 'Alimentation',
  'Cadeaux personnels', 'Autre',
];

const CONSTRAINTS = [
  { id: 'fragile',     label: '🏺 Fragile' },
  { id: 'very_fragile',label: '🔮 Très fragile' },
  { id: 'upright',     label: '⬆️ Garder à la verticale' },
  { id: 'no_stack',    label: '🚫 Ne pas empiler' },
  { id: 'temp',        label: '🌡️ Température contrôlée' },
  { id: 'cold_chain',  label: '❄️ Chaîne du froid' },
  { id: 'perishable',  label: '🥗 Produit périssable' },
  { id: 'electronic',  label: '💻 Produit électronique' },
  { id: 'valuable',    label: '💎 Produit de valeur' },
  { id: 'urgent',      label: '🚀 Livraison urgente' },
  { id: 'signature',   label: '✍️ Signature obligatoire' },
  { id: 'insurance',   label: '🛡️ Assurance recommandée' },
  { id: 'bulky',       label: '📦 Colis volumineux' },
  { id: 'careful',     label: '🤲 Manipulation avec précaution' },
  { id: 'other',       label: '📝 Autre' },
];

const CARRIER_PREFS = [
  { id: 'verified',     label: '✅ Transporteur vérifié' },
  { id: 'professional', label: '🏢 Transporteur entreprise' },
  { id: 'particular',   label: '👤 Transporteur particulier' },
  { id: 'fast',         label: '🚀 Livraison rapide' },
  { id: 'direct',       label: '🎯 Transport direct' },
  { id: 'no_stop',      label: '✈️ Sans escale' },
  { id: 'weekend',      label: '📅 Disponible le week-end' },
  { id: 'evening',      label: '🌙 Disponible en soirée' },
  { id: 'ac_vehicle',   label: '❄️ Véhicule climatisé' },
  { id: 'insured',      label: '🛡️ Assurance incluse' },
  { id: 'fragile_ok',   label: '🏺 Accepte les colis fragiles' },
  { id: 'bulky_ok',     label: '📦 Accepte les colis volumineux' },
  { id: 'french',       label: '🇫🇷 Communication en français' },
  { id: 'english',      label: '🇬🇧 Communication en anglais' },
  { id: 'experienced',  label: '⭐ Expérience confirmée' },
];

const currencySymbol = ccySym;

// ─── Formulaire principal ─────────────────────────────────────────────────────

export default function ShipmentForm() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const toast       = useToast();
  const { user }    = useAuth();
  const { id }      = useParams();
  const isEdit      = !!id;
  const isDuplicate = new URLSearchParams(location.search).get('duplicate') === '1';

  // 1 500 kg réservés aux entreprises dont le SIRET a été validé
  const isPro            = isEntreprise(user);
  const proPending       = isEntreprisePending(user);
  const maxWeightAllowed = isPro ? 1500 : 250;

  const [loading,       setLoading]       = useState(false);
  const [fetching,      setFetching]      = useState(isEdit);
  const [errors,        setErrors]        = useState([]);
  const [aiDescLoading, setAiDescLoading] = useState(false);
  const [aiMsgLoading,  setAiMsgLoading]  = useState(false);
  const [eurRates,      setEurRates]      = useState(STATIC_RATES_EUR);

  // ── Section 1 : Trajet & contexte ─────────────────────────────
  const [title,         setTitle]         = useState('');
  const [origCountry,   setOrigCountry]   = useState('');
  const [departureCity, setDepartureCity] = useState('');
  const [destCountry,   setDestCountry]   = useState('');
  const [arrivalCity,   setArrivalCity]   = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [flex,          setFlex]          = useState('3');
  const [urgency,       setUrgency]       = useState('low');
  const [description,   setDescription]   = useState('');

  // ── Section 2 : Détails colis ──────────────────────────────────
  const [categories,    setCategories]    = useState([]);
  const [categoryCustom,setCategoryCustom]= useState('');
  const [weightKg,      setWeightKg]      = useState('');
  const [pkgL,          setPkgL]          = useState('');
  const [pkgW,          setPkgW]          = useState('');
  const [pkgH,          setPkgH]          = useState('');
  const [priceOffered,  setPriceOffered]  = useState('');
  const [budgetLibre,   setBudgetLibre]   = useState(false);
  const [currencies,    setCurrencies]    = useState(['EUR']);
  const [primaryCurrency, setPrimaryCurrency] = useState('EUR');
  const [photos,        setPhotos]        = useState([]);

  // ── Section 3 : Contraintes ────────────────────────────────────
  const [constraintsList,  setConstraintsList]  = useState([]);
  const [constraintCustom, setConstraintCustom] = useState('');

  // ── Section 4 : Préférences transporteur ──────────────────────
  const [carrierPreferences, setCarrierPreferences] = useState([]);
  const [negoc,              setNegoc]              = useState('open');
  const [messageToCarriers,  setMessageToCarriers]  = useState('');

  // ── Préremplissage : édition OU duplicate ─────────────────────
  useEffect(() => {
    if (isEdit) {
      setFetching(true);
      axios.get(`${API}/shipments/${id}`)
        .then(r => {
          const s = r.data;
          setTitle(s.title || '');
          setOrigCountry(s.departure_country || '');
          setDepartureCity(s.departure_city || '');
          setDestCountry(s.arrival_country || '');
          setArrivalCity(s.arrival_city || '');
          // Annonce expirée : la republier demande une nouvelle date (le serveur refuse l'ancienne)
          setDepartureDate(s.status === 'EXPIRED' ? '' : (s.departure_date?.slice(0, 10) || ''));
          setWeightKg(s.weight_kg || '');
          setPriceOffered(s.price_offered || '');
          setBudgetLibre(!s.price_offered);
          setDescription(s.description || '');
          setCurrencies(s.currencies?.length ? s.currencies : ['EUR']);
          setPrimaryCurrency(s.primary_currency || 'EUR');
          setCategories(Array.isArray(s.categories) ? s.categories : []);
          setCategoryCustom(s.category_custom || '');
          setConstraintsList(Array.isArray(s.constraints) ? s.constraints : []);
          setConstraintCustom(s.constraint_custom || '');
          setCarrierPreferences(Array.isArray(s.carrier_preferences) ? s.carrier_preferences : []);
          setMessageToCarriers(s.message_to_carriers || '');
          setNegoc(s.counter_offers === false ? 'firm' : 'open');
          if (s.dimensions) {
            const parts = s.dimensions.split('×').map(x => x.trim());
            setPkgL(parts[0] || ''); setPkgW(parts[1] || ''); setPkgH(parts[2] || '');
          }
          if (Array.isArray(s.photos) && s.photos.length) {
            setPhotos(s.photos.map((p, i) => ({ name: `photo-${i + 1}`, dataUrl: p })));
          }
        })
        .catch(() => toast.error('Annonce introuvable'))
        .finally(() => setFetching(false));
    } else if (isDuplicate) {
      try {
        const stored = sessionStorage.getItem('c2s_shipment_duplicate');
        if (stored) {
          const d = JSON.parse(stored);
          setTitle(d.title ? `Copie — ${d.title}` : '');
          setOrigCountry(d.departure_country || '');
          setDepartureCity(d.departure_city || '');
          setDestCountry(d.arrival_country || '');
          setArrivalCity(d.arrival_city || '');
          setWeightKg(d.weight_kg || '');
          setPriceOffered(d.price_offered || '');
          setBudgetLibre(!d.price_offered);
          setDescription(d.description || '');
          setCurrencies(d.currencies?.length ? d.currencies : ['EUR']);
          setPrimaryCurrency(d.primary_currency || 'EUR');
          setCategories(Array.isArray(d.categories) ? d.categories : []);
          setCategoryCustom(d.category_custom || '');
          setConstraintsList(Array.isArray(d.constraints) ? d.constraints : []);
          setConstraintCustom(d.constraint_custom || '');
          setCarrierPreferences(Array.isArray(d.carrier_preferences) ? d.carrier_preferences : []);
          setMessageToCarriers(d.message_to_carriers || '');
          setNegoc(d.counter_offers === false ? 'firm' : 'open');
          if (d.dimensions) {
            const parts = d.dimensions.split('×').map(x => x.trim());
            setPkgL(parts[0] || ''); setPkgW(parts[1] || ''); setPkgH(parts[2] || '');
          }
          sessionStorage.removeItem('c2s_shipment_duplicate');
          toast.success('Formulaire prérempli — adaptez les informations avant de publier.');
        }
      } catch { /* sessionStorage indisponible */ }
    }
  }, [id, isEdit, isDuplicate, toast]);

  // ── Devise : ajouter / définir primaire / retirer ─────────────
  const handleCurrencyClick = (code) => {
    if (currencies.includes(code)) {
      if (code !== primaryCurrency) setPrimaryCurrency(code);
    } else {
      if (currencies.length >= 3) { toast.error('3 devises maximum.'); return; }
      setCurrencies(prev => [...prev, code]);
    }
  };

  const removeCurrency = (code) => {
    if (code === primaryCurrency) {
      toast.error('Retirez d\'abord le statut "principale" en choisissant une autre devise.');
      return;
    }
    setCurrencies(prev => prev.filter(c => c !== code));
  };

  // ── Catégories (multi, max 5) ──────────────────────────────────
  const toggleCategory = (cat) => {
    setCategories(prev => {
      if (prev.includes(cat)) return prev.filter(c => c !== cat);
      if (prev.length >= 5) { toast.error('5 catégories maximum.'); return prev; }
      return [...prev, cat];
    });
  };

  // ── Contraintes (multi, max 5) ────────────────────────────────
  const toggleConstraint = (cid) => {
    setConstraintsList(prev => {
      if (prev.includes(cid)) return prev.filter(c => c !== cid);
      if (prev.length >= 5) { toast.error('5 contraintes maximum.'); return prev; }
      return [...prev, cid];
    });
  };

  // ── Préférences transporteur (multi, max 5) ────────────────────
  const togglePreference = (pid) => {
    setCarrierPreferences(prev => {
      if (prev.includes(pid)) return prev.filter(p => p !== pid);
      if (prev.length >= 5) { toast.error('5 préférences maximum.'); return prev; }
      return [...prev, pid];
    });
  };

  // ── Taux de change live (pour minimum budget = 1 EUR équivalent) ─────────────
  useEffect(() => {
    axios.get(`${API}/currencies/rates`)
      .then(r => { if (r.data?.rates) setEurRates(prev => ({ ...prev, ...r.data.rates })); })
      .catch(() => {});
  }, []);

  // Minimum budget = 1 EUR converti dans la devise principale
  const minPrice = useMemo(
    () => calcShipmentMinPrice(primaryCurrency, eurRates),
    [eurRates, primaryCurrency],
  );

  const minPriceDisplay = useMemo(() => {
    const decimals = ZERO_DEC_CCY.has(primaryCurrency) ? 0 : 2;
    const n = minPrice.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
    const s = currencySymbol(primaryCurrency);
    if (primaryCurrency === 'EUR') return `Budget minimum ${n} ${s}.`;
    return `Budget minimum ${n} ${s} (≈ 1 €).`;
  }, [minPrice, primaryCurrency]);

  // ── Validation ────────────────────────────────────────────────
  const validate = () => {
    const errs = [];
    if (!origCountry) errs.push('Le pays de départ est requis.');
    if (!departureCity) errs.push('La ville de départ est requise.');
    if (!destCountry) errs.push("Le pays d'arrivée est requis.");
    if (!arrivalCity) errs.push("La ville d'arrivée est requise.");
    if (origCountry === destCountry && departureCity && arrivalCity &&
        departureCity.toLowerCase() === arrivalCity.toLowerCase())
      errs.push("Le départ et l'arrivée doivent être différents.");
    if (!departureDate) errs.push("La date limite d'envoi est requise.");
    if (!weightKg || +weightKg < 0.1 || +weightKg > maxWeightAllowed)
      errs.push(`Le poids doit être entre 0,1 et ${maxWeightAllowed} kg.`);
    if (!budgetLibre && (!priceOffered || +priceOffered < minPrice)) {
      const decimals = ZERO_DEC_CCY.has(primaryCurrency) ? 0 : 2;
      const minFmt = minPrice.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
      errs.push(`Le budget doit être ≥ ${minFmt} ${currencySymbol(primaryCurrency)}${primaryCurrency !== 'EUR' ? ' (≡ 1 €)' : ''}.`);
    }
    if (categories.includes('Autre') && !categoryCustom.trim())
      errs.push('Précisez la catégorie "Autre".');
    if (constraintsList.includes('other') && !constraintCustom.trim())
      errs.push('Précisez la contrainte "Autre".');
    return errs;
  };

  // ── IA — description ──────────────────────────────────────────
  const generateDescription = async () => {
    setAiDescLoading(true);
    try {
      const res = await axios.post(`${API}/ai/shipment-description`, {
        origin: departureCity ? `${departureCity}, ${origCountry}` : origCountry,
        destination: arrivalCity ? `${arrivalCity}, ${destCountry}` : destCountry,
        categories,
        weight_kg: weightKg ? parseFloat(weightKg) : undefined,
      });
      setDescription((res.data.description ?? '').slice(0, 120));
      toast.success('Description générée ✓');
    } catch {
      const cats = categories.slice(0, 2).join(', ');
      setDescription(
        `Colis ${departureCity || '?'} → ${arrivalCity || '?'}${cats ? ` — ${cats}` : '. Colis personnel'}.`.slice(0, 120)
      );
    } finally {
      setAiDescLoading(false);
    }
  };

  // ── IA — message aux transporteurs ────────────────────────────
  const generateMessage = async () => {
    setAiMsgLoading(true);
    try {
      const constraintLabels = constraintsList
        .map(cid => CONSTRAINTS.find(c => c.id === cid)?.label?.replace(/^\S+\s/, '') ?? cid);
      const prefLabels = carrierPreferences
        .map(pid => CARRIER_PREFS.find(p => p.id === pid)?.label?.replace(/^\S+\s/, '') ?? pid);
      const res = await axios.post(`${API}/ai/shipment-message`, {
        origin: departureCity || origCountry,
        destination: arrivalCity || destCountry,
        constraints: constraintLabels,
        carrier_preferences: prefLabels,
        primary_currency: primaryCurrency,
      });
      setMessageToCarriers((res.data.message ?? '').slice(0, 150));
      toast.success('Message généré ✓');
    } catch {
      setMessageToCarriers(
        `Bonjour ! Je cherche un transporteur fiable de ${departureCity || '?'} à ${arrivalCity || '?'}. N'hésitez pas à me contacter.`.slice(0, 150)
      );
    } finally {
      setAiMsgLoading(false);
    }
  };

  // ── Photos ────────────────────────────────────────────────────
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    for (const file of files) {
      // Compressée avant l'envoi (≈ 1 600 px, JPEG) : quelques centaines de Ko au lieu de plusieurs Mo (T11)
      try {
        const dataUrl = await compressImage(file);
        setPhotos(p => [...p, { name: file.name, dataUrl }]);
      } catch (err) {
        toast.error(`${file.name} : ${err.message}`);
      }
    }
    e.target.value = '';
  };
  const removePhoto = (i) => setPhotos(p => p.filter((_, k) => k !== i));

  // ── Soumission ────────────────────────────────────────────────
  const submit = async () => {
    if (!hasCardPayable(currencies)) {
      toast.error('Ajoutez une devise payable par carte : le cedi seul ne permet pas de conclure.');
      return;
    }
    const errs = validate();
    setErrors(errs);
    if (errs.length) {
      toast.error('Veuillez corriger les champs en rouge.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setLoading(true);
    try {
      const dimensions = (pkgL || pkgW || pkgH) ? `${pkgL || '?'}×${pkgW || '?'}×${pkgH || '?'} cm` : '';
      const selectedCategories = categories.includes('Autre') && categoryCustom.trim()
        ? [...categories.filter(c => c !== 'Autre'), categoryCustom.trim()]
        : [...categories];

      const payload = {
        title: (title || `Colis ${departureCity} → ${arrivalCity}`).slice(0, 50),
        description: (description || (selectedCategories.length ? `Catégories : ${selectedCategories.join(', ')}` : 'Colis personnel')).slice(0, 120),
        departure_city: departureCity,
        arrival_city: arrivalCity,
        departure_country: origCountry,
        arrival_country: destCountry,
        departure_date: new Date(departureDate).toISOString(),
        weight_kg: parseFloat(weightKg),
        dimensions,
        price_offered: budgetLibre ? null : parseFloat(priceOffered),
        primary_currency: primaryCurrency,
        currencies,
        categories: selectedCategories,
        category_custom: categoryCustom || null,
        constraints: constraintsList,
        constraint_custom: constraintCustom || null,
        carrier_preferences: carrierPreferences,
        message_to_carriers: messageToCarriers || null,
        // « Budget ferme » : pas de contre-offre (T29 — le choix n'était jamais envoyé)
        counter_offers: negoc !== 'firm',
        photos: photos.map(p => p.dataUrl),
      };

      if (isEdit) {
        await axios.patch(`${API}/shipments/${id}`, payload);
        toast.success('Annonce mise à jour — en attente de validation ✓');
        navigate(`/app/shipments/${id}`);
      } else {
        const res = await axios.post(`${API}/shipments`, payload);
        toast.success('Annonce publiée — en attente de validation ✓');
        navigate(`/app/shipments/${res.data.id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => (typeof e === 'string' ? e : e.msg || JSON.stringify(e))).join(': ')
        : (typeof detail === 'string' ? detail : 'Erreur lors de la publication');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Récap sidebar ─────────────────────────────────────────────
  const sym = currencySymbol(primaryCurrency);

  const weightDisplay = useMemo(() => (weightKg ? `${parseFloat(weightKg).toFixed(1)} kg` : '·'), [weightKg]);
  const budgetDisplay = useMemo(() => (budgetLibre ? 'Budget libre' : priceOffered ? `${parseFloat(priceOffered).toFixed(0)} ${sym}` : '·'), [budgetLibre, priceOffered, sym]);
  const routeDisplay  = useMemo(() => {
    const o = departureCity || (origCountry ? COUNTRIES.find(c => c.code === origCountry)?.name || origCountry : '?');
    const d = arrivalCity   || (destCountry ? COUNTRIES.find(c => c.code === destCountry)?.name || destCountry : '?');
    if (o !== '?' || d !== '?') return `${o} → ${d}`;
    return 'Trajet à définir';
  }, [departureCity, arrivalCity, origCountry, destCountry]);

  const hasError = (kind) => errors.some(e => {
    if (kind === 'origCountry')   return e.includes('pays de départ');
    if (kind === 'from')          return e.includes('ville de départ');
    if (kind === 'destCountry')   return e.includes("pays d'arrivée");
    if (kind === 'to')            return e.includes("ville d'arrivée") || e.includes('différents');
    if (kind === 'date')          return e.includes('date');
    if (kind === 'weight')        return e.includes('poids');
    if (kind === 'price')         return e.includes('budget');
    if (kind === 'catCustom')     return e.includes('catégorie');
    if (kind === 'constCustom')   return e.includes('contrainte');
    return false;
  });

  if (fetching) return <div style={{ padding: 32, color: 'var(--ink3)' }}>Chargement…</div>;

  if (!isEdit && user && !canDo(user, 'publish')) {
    return (
      <div className="sf-wrap">
        <div className="sf-head">
          <div className="sf-head-title">Publier un colis</div>
        </div>
        <VerificationGate user={user} action="publish" goal="publier un colis" />
      </div>
    );
  }

  return (
    <div className="sf-wrap">
      <div className="sf-head">
        <div>
          <div className="sf-head-title">
            {isEdit ? "Modifier l'annonce" : isDuplicate ? 'Dupliquer une annonce' : 'Publier un colis'}
          </div>
          <div className="sf-head-sub">
            {isPro
              ? '✔ Compte Entreprise — jusqu\'à 1 500 kg'
              : proPending
                ? 'Entreprise en cours de vérification — jusqu\'à 250 kg (1 500 kg une fois votre SIRET validé)'
                : 'Compte Particulier — jusqu\'à 250 kg'}
            {isDuplicate && ' · Formulaire prérempli — adaptez avant de publier.'}
          </div>
        </div>
        <button type="button" className="sf-btn sf-btn-ghost" style={{ width: 'auto' }}
          onClick={() => navigate('/app/shipments')} data-testid="btn-cancel">Annuler</button>
      </div>

      <div className="sf-grid">
        <div className="sf-main">
          {errors.length > 0 && (
            <div className="sf-err-box" data-testid="error-box">
              <strong>Quelques champs nécessitent votre attention :</strong>
              <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}

          {/* ── Section 1 : Trajet & contexte d'envoi ──────────── */}
          <div className="sf-card">
            <div className="sf-card-hd"><span className="sf-step-badge">1</span>Trajet &amp; contexte d'envoi</div>
            <div className="sf-card-bd">

              <div className="sf-field">
                <label className="sf-label" htmlFor="f-title">Titre de l'annonce</label>
                <input id="f-title" className="sf-input" type="text"
                  placeholder="Ex : Colis personnel pour Dakar"
                  value={title}
                  onChange={e => setTitle(e.target.value.slice(0, 50))}
                  maxLength={50}
                  data-testid="input-title" />
                <div className="sf-char-count">{title.length}/50</div>
              </div>

              <div className="sf-row">
                <CountryCitySelector
                  dirLabel="Départ"
                  country={origCountry}
                  onCountryChange={setOrigCountry}
                  city={departureCity}
                  onCityChange={setDepartureCity}
                  errCountry={hasError('origCountry')}
                  errCity={hasError('from')}
                />
                <CountryCitySelector
                  dirLabel="Arrivée"
                  country={destCountry}
                  onCountryChange={setDestCountry}
                  city={arrivalCity}
                  onCityChange={setArrivalCity}
                  errCountry={hasError('destCountry')}
                  errCity={hasError('to')}
                />
              </div>

              <div className="sf-row">
                <div className="sf-field">
                  <label className="sf-label" htmlFor="f-date">Date limite d'envoi <span className="sf-req">*</span></label>
                  <input id="f-date" className={`sf-input ${hasError('date') ? 'err' : ''}`} type="date"
                    value={departureDate} onChange={e => setDepartureDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)} data-testid="input-date" />
                </div>
                <div className="sf-field">
                  <label className="sf-label" htmlFor="f-flex">Flexibilité</label>
                  <select id="f-flex" className="sf-select" value={flex} onChange={e => setFlex(e.target.value)}>
                    <option value="0">Date exacte</option>
                    <option value="1">± 1 jour</option>
                    <option value="3">± 3 jours</option>
                    <option value="7">± 1 semaine</option>
                    <option value="30">Flexible (1 mois)</option>
                  </select>
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">Niveau d'urgence</label>
                <div className="sf-seg">
                  {Object.entries(URGENCY_HINTS).map(([k, v]) => (
                    <button key={k} type="button"
                      className={`sf-seg-btn ${urgency === k ? 'on' : ''}`}
                      onClick={() => setUrgency(k)} data-testid={`urgency-${k}`}>{v.label}</button>
                  ))}
                </div>
                <div className="sf-hint">{URGENCY_HINTS[urgency].hint}</div>
              </div>

              <div className="sf-field">
                <label className="sf-label" htmlFor="f-desc">Description générale</label>
                <div className="sf-ai-row">
                  <textarea id="f-desc" className="sf-textarea" rows={3}
                    placeholder="Contexte de l'envoi, informations utiles pour le transporteur..."
                    value={description}
                    onChange={e => setDescription(e.target.value.slice(0, 120))}
                    data-testid="input-description" />
                  <button type="button" className="sf-btn-ai" onClick={generateDescription}
                    disabled={aiDescLoading} title="Générer avec l'IA">
                    {aiDescLoading ? '…' : '✨ IA'}
                  </button>
                </div>
                <div className="sf-char-count">{description.length}/120</div>
              </div>
            </div>
          </div>

          {/* ── Section 2 : Détails du colis ────────────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd"><span className="sf-step-badge">2</span>Détails du colis</div>
            <div className="sf-card-bd">

              <div className="sf-field">
                <label className="sf-label">
                  Catégorie(s)
                  <span style={{ fontWeight: 400, color: 'var(--ink3)', marginLeft: 6 }}>
                    ({categories.length}/5)
                  </span>
                </label>
                <div className="sf-checks">
                  {CATEGORIES.map(cat => {
                    const isOn      = categories.includes(cat);
                    const isDisabled = !isOn && categories.length >= 5;
                    return (
                      <label key={cat} className={`sf-check ${isDisabled ? 'disabled' : ''}`}>
                        <input type="checkbox" checked={isOn}
                          onChange={() => toggleCategory(cat)}
                          disabled={isDisabled}
                          data-testid={`category-${cat}`} />
                        <span>{cat}</span>
                      </label>
                    );
                  })}
                </div>
                {categories.includes('Autre') && (
                  <div className="sf-custom-input">
                    <input type="text"
                      className={`sf-input ${hasError('catCustom') ? 'err' : ''}`}
                      placeholder='Précisez (ex. "Jouets", "Pièces détachées"…)'
                      value={categoryCustom}
                      onChange={e => setCategoryCustom(e.target.value)}
                      maxLength={60} />
                  </div>
                )}
              </div>

              <div className="sf-row">
                <div className="sf-field">
                  <label className="sf-label" htmlFor="f-weight">Poids (kg) <span className="sf-req">*</span></label>
                  <input id="f-weight" className={`sf-input ${hasError('weight') ? 'err' : ''}`}
                    type="number" step="0.1" min="0.1" max={maxWeightAllowed}
                    placeholder="Ex : 3.5"
                    value={weightKg} onChange={e => setWeightKg(e.target.value)}
                    data-testid="input-weight" />
                  <div className="sf-hint">
                    Entre 0,1 et {maxWeightAllowed} kg ({isPro ? 'Entreprise' : proPending ? 'Entreprise en vérification' : 'Particulier'}).
                  </div>
                </div>
                <div className="sf-field">
                  <label className="sf-label">Dimensions (cm)</label>
                  <div className="sf-dims">
                    <input className="sf-input" type="number" placeholder="L"
                      value={pkgL} onChange={e => setPkgL(e.target.value)} aria-label="Longueur" />
                    <input className="sf-input" type="number" placeholder="l"
                      value={pkgW} onChange={e => setPkgW(e.target.value)} aria-label="Largeur" />
                    <input className="sf-input" type="number" placeholder="H"
                      value={pkgH} onChange={e => setPkgH(e.target.value)} aria-label="Hauteur" />
                  </div>
                </div>
              </div>

              {/* Devise */}
              <div className="sf-field">
                <label className="sf-label">
                  Devise <span className="sf-req">*</span>
                  <span style={{ fontWeight: 400, color: 'var(--ink3)', marginLeft: 6 }}>
                    ({currencies.length}/3 · ★ = principale · clic pour choisir · × pour retirer)
                  </span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {CURRENCIES_LIST.map(cur => {
                    const isSelected = currencies.includes(cur.code);
                    const isPrimary  = cur.code === primaryCurrency;
                    const isDisabled = !isSelected && currencies.length >= 3;
                    return (
                      <button key={cur.code} type="button"
                        className={`sf-badge ${isPrimary ? 'primary' : isSelected ? 'secondary' : ''}`}
                        style={isDisabled ? { opacity: .4, cursor: 'not-allowed' } : {}}
                        onClick={() => !isDisabled && handleCurrencyClick(cur.code)}
                        title={
                          isPrimary  ? `${cur.name} — devise principale` :
                          isSelected ? `Cliquer pour définir ${cur.code} comme principale` :
                          `Ajouter ${cur.name}`
                        }
                      >
                        {isPrimary ? '★ ' : ''}{cur.symbol} {cur.code}
                        {isSelected && !isPrimary && (
                          <span
                            role="button"
                            aria-label={`Retirer ${cur.code}`}
                            style={{ marginLeft: 5, fontWeight: 800, fontSize: 12, lineHeight: 1, opacity: .7 }}
                            onClick={e => { e.stopPropagation(); removeCurrency(cur.code); }}
                          >×</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!hasCardPayable(currencies) && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }} data-testid="sf-no-card-payable">
                    Le cedi ne peut pas être payé par carte : il sert à afficher votre prix. Ajoutez une devise payable
                    (par exemple l'euro ou le franc CFA) pour que l'on puisse vous faire une proposition.
                  </div>
                )}
                <div className="sf-hint">
                  Cliquez sur une devise pour l'ajouter. Cliquez sur une devise sélectionnée pour la définir comme principale (★).
                </div>
              </div>

              {/* Budget avec devise dynamique */}
              <div className="sf-field">
                <label className="sf-label" htmlFor="f-budget">
                  Budget proposé ({sym}) {!budgetLibre && <span className="sf-req">*</span>}
                </label>
                {!budgetLibre && (
                  <>
                    <input id="f-budget" className={`sf-input ${hasError('price') ? 'err' : ''}`}
                      type="number" step="1" min={minPrice}
                      placeholder={`Ex : ${Math.max(40, Math.round(minPrice * 3))}`}
                      value={priceOffered} onChange={e => setPriceOffered(e.target.value)}
                      data-testid="input-price" />
                    <div className="sf-hint">{minPriceDisplay}</div>
                  </>
                )}
                <label className="sf-check-row" style={{ marginTop: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={budgetLibre}
                    onChange={e => { setBudgetLibre(e.target.checked); if (e.target.checked) setPriceOffered(''); }} />
                  <span>Budget libre. Laisser les transporteurs proposer leur tarif</span>
                </label>
              </div>

              <div className="sf-field">
                <label className="sf-label">Photos du colis (3 au plus, réduites automatiquement)</label>
                <div className="sf-photos">
                  {photos.map((p, i) => (
                    <div key={i} className="sf-photo">
                      <img src={p.dataUrl} alt={p.name} />
                      <button type="button" className="sf-photo-del" onClick={() => removePhoto(i)}
                        aria-label="Supprimer la photo">×</button>
                    </div>
                  ))}
                  {photos.length < 3 && (
                    <label className="sf-photo-add">
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile}
                        style={{ display: 'none' }} data-testid="input-photos" />+
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 3 : Contraintes du colis ────────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd">
              <span className="sf-step-badge">3</span>
              Contraintes du colis
              <span style={{ fontWeight: 400, color: 'var(--ink3)', marginLeft: 8, fontSize: 13 }}>
                ({constraintsList.length}/5)
              </span>
            </div>
            <div className="sf-card-bd">
              <div className="sf-checks">
                {CONSTRAINTS.map(c => {
                  const isOn      = constraintsList.includes(c.id);
                  const isDisabled = !isOn && constraintsList.length >= 5;
                  return (
                    <label key={c.id} className={`sf-check ${isDisabled ? 'disabled' : ''}`}>
                      <input type="checkbox" checked={isOn}
                        onChange={() => toggleConstraint(c.id)}
                        disabled={isDisabled}
                        data-testid={`constraint-${c.id}`} />
                      <span>{c.label}</span>
                    </label>
                  );
                })}
              </div>
              {constraintsList.includes('other') && (
                <div className="sf-custom-input" style={{ marginTop: 12 }}>
                  <input type="text"
                    className={`sf-input ${hasError('constCustom') ? 'err' : ''}`}
                    placeholder='Précisez la contrainte (ex. "Ne pas exposer au soleil"…)'
                    value={constraintCustom}
                    onChange={e => setConstraintCustom(e.target.value)}
                    maxLength={80} />
                </div>
              )}
            </div>
          </div>

          {/* ── Section 4 : Préférences transporteur ─────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd">
              <span className="sf-step-badge">4</span>
              Préférences transporteur
              <span style={{ fontWeight: 400, color: 'var(--ink3)', marginLeft: 8, fontSize: 13 }}>
                ({carrierPreferences.length}/5)
              </span>
            </div>
            <div className="sf-card-bd">
              <div className="sf-checks">
                {CARRIER_PREFS.map(p => {
                  const isOn      = carrierPreferences.includes(p.id);
                  const isDisabled = !isOn && carrierPreferences.length >= 5;
                  return (
                    <label key={p.id} className={`sf-check ${isDisabled ? 'disabled' : ''}`}>
                      <input type="checkbox" checked={isOn}
                        onChange={() => togglePreference(p.id)}
                        disabled={isDisabled}
                        data-testid={`pref-${p.id}`} />
                      <span>{p.label}</span>
                    </label>
                  );
                })}
              </div>

              <div className="sf-row" style={{ marginTop: 16 }}>
                <div className="sf-field">
                  <label className="sf-label" htmlFor="negoc">Négociation</label>
                  <select id="negoc" className="sf-select" value={negoc} onChange={e => setNegoc(e.target.value)}>
                    <option value="open">Ouvert aux contre-offres</option>
                    <option value="firm">Budget ferme</option>
                  </select>
                </div>
              </div>

              <div className="sf-field" style={{ marginTop: 8 }}>
                <label className="sf-label" htmlFor="f-message">Message aux transporteurs</label>
                <div className="sf-ai-row">
                  <textarea id="f-message" className="sf-textarea" rows={3}
                    placeholder="Instructions de remise, contact à l'arrivée, précisions utiles…"
                    value={messageToCarriers}
                    onChange={e => setMessageToCarriers(e.target.value.slice(0, 150))}
                    maxLength={150}
                    data-testid="input-message" />
                  <button type="button" className="sf-btn-ai" onClick={generateMessage}
                    disabled={aiMsgLoading} title="Rédiger avec l'IA">
                    {aiMsgLoading ? '…' : '✨ IA'}
                  </button>
                </div>
                <div className="sf-char-count">{messageToCarriers.length}/150</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Récap sidebar ────────────────────────────────────── */}
        <aside className="sf-aside">
          <div className="sf-recap">
            <div className="sf-recap-hero">
              <div className="sf-recap-eyebrow">Annonce expéditeur</div>
              <div className="sf-recap-title">{title || 'Colis à préparer'}</div>
              <div className="sf-recap-route">{routeDisplay}</div>
            </div>
            <div className="sf-recap-stats">
              <div className="sf-recap-stat">
                <div className="sf-recap-stat-label">Poids</div>
                <div className="sf-recap-stat-value" data-testid="recap-weight">{weightDisplay}</div>
              </div>
              <div className="sf-recap-stat">
                <div className="sf-recap-stat-label">Budget</div>
                <div className="sf-recap-stat-value" data-testid="recap-budget">{budgetDisplay}</div>
              </div>
            </div>
            <div className="sf-recap-body">
              <div className="sf-recap-line">
                <span className="k">Date limite</span>
                <span className="v">{departureDate ? new Date(departureDate).toLocaleDateString('fr-FR') : '·'}</span>
              </div>
              <div className="sf-recap-line">
                <span className="k">Urgence</span>
                <span className="v">{URGENCY_HINTS[urgency].label}</span>
              </div>
              {categories.length > 0 && (
                <div className="sf-recap-line">
                  <span className="k">Catégories</span>
                  <span className="v">{categories.length}</span>
                </div>
              )}
              {(pkgL || pkgW || pkgH) && (
                <div className="sf-recap-line">
                  <span className="k">Dimensions</span>
                  <span className="v">{`${pkgL || '?'}×${pkgW || '?'}×${pkgH || '?'} cm`}</span>
                </div>
              )}
              {constraintsList.length > 0 && (
                <div className="sf-recap-line">
                  <span className="k">Contraintes</span>
                  <span className="v">{constraintsList.length}</span>
                </div>
              )}
              {photos.length > 0 && (
                <div className="sf-recap-line">
                  <span className="k">Photos</span>
                  <span className="v">{photos.length}</span>
                </div>
              )}
              {currencies.length > 1 && (
                <div className="sf-recap-line">
                  <span className="k">Devises</span>
                  <span className="v">{currencies.map(c => c === primaryCurrency ? `★ ${c}` : c).join(', ')}</span>
                </div>
              )}
            </div>
            <div className="sf-escrow">
              <span aria-hidden="true">🔒</span>
              <span>Le paiement sera sécurisé par séquestre. La mission ne s'active qu'après confirmation.</span>
            </div>
            <div className="sf-actions">
              <button type="button" className="sf-btn sf-btn-primary" onClick={submit}
                disabled={loading} data-testid="btn-submit">
                {loading ? 'Publication…' : (isEdit ? "Mettre à jour l'annonce" : "Publier l'annonce")}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
